import React, { ReactNode, useMemo } from "react";
import styled from "@emotion/styled";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { Box, Typography, TypographyProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  AccountSelectGroup,
  AccountsApiListAndSearchAllAccountsRequest,
  AccountsApiSelectGroupsIdRequest,
  PublicAccount,
} from "@bn/api/apiv1";

const DepotSelectorIcon = styled(KeyboardArrowDownRoundedIcon)`
  color: currentColor;
`;

export type Props = {
  accounts?: PublicAccount[];
  selectedGroup?: AccountSelectGroup;
  accountParams?: AccountsApiListAndSearchAllAccountsRequest;
  selectedGroupParams: AccountsApiSelectGroupsIdRequest;
  color?: string;
  textKeys?: {
    allDepots: string;
    amountDepots: string;
    noDepots: string;
  };
  icon?: ReactNode;
  onButtonClick: () => void;
} & TypographyProps;

export const DepotsSelectorButton = ({
  accounts,
  selectedGroup,
  accountParams,
  selectedGroupParams,
  color = "text.primary",
  textKeys = {
    allDepots: "depotsSelector.allPortfolios",
    amountDepots: "depotsSelector.amountPortfolios",
    noDepots: "depotsSelector.noPortfolios",
  },
  icon,
  onButtonClick,
  ...rest
}: Props) => {
  const { t } = useTranslation();

  const selectableAccounts = useMemo(
    () =>
      accounts?.filter(
        (account) =>
          !account.depotIds?.length ||
          !accounts?.some((acc) => account.depotIds?.includes(acc.id))
      ) ?? [],
    [accounts]
  );

  const checkedPortfolios = useMemo(
    () =>
      (selectedGroup?.accountIds ?? []).filter((id) =>
        accounts?.some((x) => x.id === id)
      ),
    [selectedGroup?.accountIds, accounts]
  );

  const amountSelectableAccounts = selectableAccounts?.length ?? 0;
  const amountSelectedAccounts =
    selectableAccounts?.filter((x) => checkedPortfolios.includes(x.id))
      .length ?? 0;

  const depotsSelectorText =
    amountSelectedAccounts === amountSelectableAccounts
      ? t(textKeys.allDepots)
      : t(textKeys.amountDepots, {
          selected: amountSelectedAccounts,
          total: amountSelectableAccounts,
        });

  return (
    <Box
      onClick={() => Boolean(amountSelectableAccounts) && onButtonClick()}
      data-testid={
        amountSelectableAccounts ? "select-depots" : "no-select-depots"
      }
    >
      <Box
        display="inline-flex"
        alignItems="center"
        color="currentColor"
        sx={{
          height: "27px",
          cursor: "pointer",
        }}
      >
        {amountSelectableAccounts !== 0 ? (
          <>
            <Typography variant="body2" {...rest}>
              {!amountSelectedAccounts
                ? t(textKeys.allDepots)
                : depotsSelectorText}
            </Typography>
            {icon ? icon : <DepotSelectorIcon />}
          </>
        ) : (
          <Typography variant="body4" {...rest}>
            {t(textKeys.noDepots)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
